import { computed, ComputedRef, defineComponent, nextTick, onBeforeUnmount, onMounted, Ref, ref, StyleValue, watch, useModel } from "vue";
import { SkywardException } from "../../skyward.base.error";
import Button from "../core/button";
import Loading from "../core/loading";
import Svg from "../core/svg";
import { DefaultVueProp, OptionalVueProp, VueEmit } from "../core/vueHelpers";
import { nextAutoId } from "../../skyward.base.extensions";

const classNames =
{
    container: "quickEELSelector__container",
    containerOpen: "quickEELSelector__container--open",
    button: "quickEELSelector__button",
    menuContainer: "quickEELSelector__menuContainer",
    controlContainer: "ui-control-container",
    searchContainer: "quickEELSelector__searchContainer",
    searchbox: "quickEELSelector__searchbox",
    searchSvg: "quickEELSelector__searchSvg",
    listContainer: "quickEELSelector__listContainer",
    listItem: "quickEELSelector__listItem",
    listItemIndented: "quickEELSelector__listItem--indented",
    listItemSelected: "quickEELSelector__listItem--selected",
    listItemDisabled: "quickEELSelector__listItem--disabled",
    link: "quickEELSelector__link",
    noResults: "quickEELSelector__noResults",
}

export interface QuickEELOption
{
    value: string | number;
    text: string;
    title?: string;
    disabled?: boolean;
    indent?: boolean;
    heading?: boolean;
    ariaLabel?: string;
    isDistrict?: boolean;
}

interface QuickEELOptionWithId extends QuickEELOption
{
    id: string;
    visible: boolean;
}

export default defineComponent(
    {
        props:
        {
            value: OptionalVueProp<string | number | Ref<number, number>>([String, Number, Object]),
            text: OptionalVueProp<string | Ref<string, string>>([String, Object]),
            title: OptionalVueProp<string | Ref<number, number>>([String, Object]),
            ariaLabel: OptionalVueProp(String),
            readonly: OptionalVueProp(Boolean),
            options: OptionalVueProp<(() => Promise<QuickEELOption[]>) | QuickEELOption[]>([Function, Array]),
            additionalButtonClasses: OptionalVueProp(String),
            popupMaxHeight: OptionalVueProp(String),
            role: DefaultVueProp("combobox")
        },
        emits:
        {
            "update:value": VueEmit<(value: string | number) => void>(),
        },
        setup(props, context)
        {
            const containerRef = ref<HTMLElement>();
            const buttonRef = ref<HTMLElement>();
            const selectedValue = ref<string | number>(props.value as string | number ?? "");
            const tempSelectedValue = ref<string | number>(props.value as string | number ?? "");
            const selectedText = ref<string>(props.text as string ?? "");
            const staticTitle = ref<string>(props.title as string ?? "");
            const options = ref<QuickEELOption[]>(new Array());
            const isOpen = ref<boolean>(false);
            const searchInputRef = ref<HTMLInputElement>();
            const searchText = ref<string>();
            const loaded = ref<boolean>(false);
            const value = useModel(props, "value");
            const optionsWithIds = ref<Map<string | number, string>>(new Map()); // id's are needed for aria-activedescendant to work properly

            // Initialize options if they're provided synchronously
            if (Array.isArray(props.options))
            {
                options.value = props.options;
            }

            watch(isOpen, (newValue) =>
            {
                if (newValue)
                {
                    // use a setTimeout to prevent the menu from closing from the click event that is in progress
                    setTimeout(() =>
                    {
                        document.body.addEventListener("click", autoClosePopup);
                    }, 0);
                }
                else
                {
                    document.body.removeEventListener("click", autoClosePopup);
                }
            }, { immediate: true });

            onBeforeUnmount(() =>
            {
                document.body.removeEventListener("click", autoClosePopup);
            });

            const computedOptions: ComputedRef<(QuickEELOptionWithId)[]> = computed(() =>
            {
                const compOptions = options.value.map(option =>
                {
                    const isVisible = !searchText.value || option.text.toLowerCase().includes(searchText.value.toLowerCase());
                    
                    // Get or create ID's needed for accessibility (aria-activedescendant)
                    let optionId = optionsWithIds.value.get(option.value);
                    if (!optionId) {
                        optionId = `quickeel-option-${option.value}-${nextAutoId()}`;
                        optionsWithIds.value.set(option.value, optionId);
                    }

                    return { 
                        ...option, 
                        visible: isVisible,
                        id: optionId
                    };
                });

                //show the headings for visible items
                let showNextHeading = false;
                for (let i = compOptions.length - 1; i >= 0; i--)
                {
                    if (compOptions[i].visible && !compOptions[i].heading)
                    {
                        showNextHeading = true;
                        continue;
                    }
                    if (compOptions[i].heading && showNextHeading)
                    {
                        compOptions[i].visible = true;
                        showNextHeading = false;
                    }
                }
                return compOptions;
            });

            const activeDescendantId = computed(() =>
            {
                const tempSelected = computedOptions.value.find(option => `${option.value}` === `${tempSelectedValue.value}`);
                return tempSelected?.id || "";
            });

            let buttonClasses = classNames.button;
            if (props.additionalButtonClasses)
            {
                buttonClasses = buttonClasses.concat(` ${props.additionalButtonClasses}`);
            }

            const popupStyle: StyleValue = {};

            if (props.popupMaxHeight)
            {
                popupStyle.maxHeight = props.popupMaxHeight;
            }
            const menuContainerId = nextAutoId();

            return () => (
                <div ref={containerRef} class={{ [classNames.container]: true, [classNames.containerOpen]: isOpen.value }}>
                    <Button
                        ref={buttonRef}
                        svgName="ArrowTriangleDown"
                        class={buttonClasses}
                        clickFunc={togglePopup}
                        svgShowHoverEffect={false}
                        svgOnRight={true}
                        title={staticTitle.value}
                        disabled={props.readonly === true}
                        role={props.role}
                        aria-expanded={isOpen.value}
                        aria-label={props.ariaLabel}
                        aria-controls={menuContainerId}
                        aria-activedescendant={activeDescendantId.value}
                    >
                        {selectedText.value}
                    </Button>
                    <div class={{ [classNames.menuContainer]: true, "popup": true } }>
                        <div id={menuContainerId} class={{ [classNames.searchContainer]: true, [classNames.controlContainer]: true }}>
                            <input role="searchbox" class={classNames.searchbox} ref={searchInputRef} type="text" autocomplete="none"
                                aria-autocomplete="list"
                                aria-activedescendant={activeDescendantId.value}
                                onKeyup={search}
                                onKeypress={(event) => { event.stopPropagation(); }}
                                // use keydown here so the click event from the button doesn't trigger a keyup event on this input when the popup is opened
                                onKeydown={changeTempSelection}
                            />
                            <Svg svgClasses={[classNames.searchSvg]} name="Search" tabindex="-1" showHoverEffect={false} />
                        </div>
                        {
                            !loaded.value && <Loading />
                        }
                        <ul class={classNames.listContainer} style={popupStyle} role="listbox">
                            {computedOptions.value?.map(option =>
                            {
                                const isVisuallySelected = `${tempSelectedValue.value}` === `${option.value}`;
                                const isAriaSelected = `${selectedValue.value}` === `${option.value}`;
                                return <li class={{
                                    [classNames.listItem]: true,
                                    [classNames.listItemSelected]: isVisuallySelected,
                                    [classNames.listItemIndented]: option.indent,
                                    [classNames.listItemDisabled]: option.disabled,
                                }}
                                    role="option"
                                    id={option.id}
                                    aria-label={option.ariaLabel ?? option.text}
                                    aria-selected={isAriaSelected}
                                    style={{ display: option.visible ? "block" : "none" }}
                                    data-value={`${option.value}`}>
                                    <a class={classNames.link} href="javascript:void(0)" tabindex="-1" onClick={selectOption.bind(option)} >
                                        {option.text}
                                    </a>
                                </li>;
                            })}
                        </ul>
                        <div class={classNames.noResults} style={{ display: tempSelectedValue.value === undefined ? "block" : "none" }}>No records to display</div>
                    </div>
                </div>
            );

            function selectOption(this: QuickEELOption)
            {
                if (this.disabled === true)
                {
                    return;
                }
                const changed = selectedValue.value != this.value;
                const numberValue = parseInt(this.value as string);
                selectedValue.value = isNaN(numberValue) ? this.value : numberValue;
                selectedText.value = this.text;
                value.value = selectedValue.value;
                if (changed)
                {
                    $(containerRef.value as HTMLElement).trigger("change", { value: selectedValue.value, isDistrict: this.isDistrict ?? false });
                }
                closePopup(true);
            }

            function selectValue(valueToSelect: string | number)
            {
                const selectedOption = options.value.find(o => o.value?.toString() === valueToSelect?.toString());
                if (selectedOption)
                {
                    selectOption.call(selectedOption);
                }
            }

            function search(event: KeyboardEvent)
            {
                event.stopPropagation();

                searchText.value = searchInputRef.value?.value ?? "";
                nextTick(() =>
                {
                    const containerElement = $(containerRef.value as HTMLElement);
                    let selected = containerElement.find(`.${classNames.listItemSelected}`);
                    if (!selected.is(":visible"))
                    {
                        tempSelectedValue.value = containerElement.find(`.${classNames.listItem}:visible`).not(`.${classNames.listItemDisabled}`).first().data("value");
                    }
                });
            }

            function changeTempSelection(event: KeyboardEvent)
            {
                event.stopPropagation();

                const containerElement = $(containerRef.value as HTMLElement);
                let selected = containerElement.find(`.${classNames.listItemSelected}`);
                let tempSelected: JQuery<HTMLElement>;
                switch (event.key)
                {
                    case "ArrowDown":
                        tempSelected = selected.nextAll(":visible").not(`.${classNames.listItemDisabled}`).first()
                        if (!tempSelected.length)
                        {
                            tempSelected = containerElement.find(`.${classNames.listItem}:visible`).not(`.${classNames.listItemDisabled}`).last();
                        }
                        tempSelectedValue.value = tempSelected.data("value");
                        tempSelected.scrollintoview();
                        break;
                    case "ArrowUp":
                        tempSelected = selected.prevAll(":visible").not(`.${classNames.listItemDisabled}`).first()
                        if (!tempSelected.length)
                        {
                            tempSelected = containerElement.find(`.${classNames.listItem}:visible`).not(`.${classNames.listItemDisabled}`).first();
                        }
                        tempSelectedValue.value = tempSelected.data("value");
                        tempSelected.scrollintoview();
                        break;
                    case "Escape":
                        tempSelectedValue.value = selectedValue.value;
                        closePopup(false);
                        break;
                    case "Tab":
                        if (tempSelectedValue.value !== undefined)
                        {
                            selectValue(tempSelectedValue.value);
                        }
                        break;
                    case "Enter":
                        event.preventDefault();
                        if (tempSelectedValue.value !== undefined)
                        {
                            selectValue(tempSelectedValue.value);
                        }
                        break;
                }
            }

            async function openPopup()
            {
                if (props.readonly === true)
                {
                    return;
                }
                isOpen.value = true;
                tempSelectedValue.value = selectedValue.value;
                nextTick(() => searchInputRef.value?.focus());
                if (!options.value.length)
                {
                    if (props.options)
                    {
                        if (typeof props.options === "function")
                        {
                            options.value = await props.options();
                        }
                        else
                        {
                            options.value = props.options;
                        }
                    }
                    else
                    {
                        throw new SkywardException("Options were not provided to the QuickEELSelector");
                    }

                    loaded.value = true;
                }
                else
                {
                    loaded.value = true;
                }
                
                // Ensure the DOM has been updated with the list items
                await nextTick();
                const containerElement = $(containerRef.value as HTMLElement);
                containerElement.find(`.${classNames.listItemSelected}`).scrollintoview();
            }

            function closePopup(shouldRefocus: boolean = true)
            {
                isOpen.value = false;
                tempSelectedValue.value = selectedValue.value;
                searchText.value = "";
                if (searchInputRef?.value)
                {
                    searchInputRef.value.value = "";
                }

                if (shouldRefocus)
                {
                    // Use nextTick to ensure the popup is fully closed before refocusing
                    nextTick(() =>
                    {
                        buttonRef.value?.focus();
                    });
                }
            }

            function autoClosePopup(e: Event)
            {
                if (!containerRef.value?.contains(e.target as HTMLElement))
                {
                    closePopup(false);
                }
            }

            function togglePopup()
            {
                isOpen.value ? closePopup(false) : openPopup();
            }
        }
    }
);